/**
 * Parallel chunked-upload helper.
 *
 * The previous implementation sliced a file into 20MB blocks and awaited each
 * block inside a `for` loop, so exactly one request was ever in flight. Between
 * blocks the connection sat idle waiting for the server to write the chunk and
 * respond, and a single stream could never fill a high bandwidth-delay-product
 * link. Uploading several blocks concurrently keeps the pipe busy and is worth
 * roughly 3-5x on the same connection.
 *
 * Blocks are claimed from a shared cursor by a fixed pool of workers, so a slow
 * block delays only its own worker rather than everything behind it.
 */

// 10MB. Smaller than the old 20MB because blocks now overlap: more, smaller
// blocks give the pool finer-grained work to balance and make a failed block
// cheaper to lose. Also stays >= the 5MB S3 multipart minimum, so this size
// carries over unchanged if uploads later move to presigned multipart.
export const CHUNK_SIZE = 1048576 * 10;

// Measured on a 1.5GB upload: ~8.6 Mbps per stream, well under what one TCP
// stream can carry. UploadChunk is store-and-forward - the server takes the whole
// block, relays it to S3, then ACKs - so streams are latency-bound, not
// bandwidth-bound, and extra ones convert into throughput almost linearly.
// 8 is past the 6-connection cap browsers apply on HTTP/1.1 (the extra two just
// queue there, harmlessly) and pays off on HTTP/2, which has no such cap.
export const CHUNK_CONCURRENCY = 8;

// Attempts per block, the first one included. A block that fails takes the whole
// run down with it, and on a large file that means re-sending everything already
// up - a 1.5GB upload dying at 96% because one request hit a 504 or a dropped
// connection. Blocks are independent and the endpoint keys each by its order id,
// so re-sending one is safe: S3 overwrites the part with that number.
export const CHUNK_ATTEMPTS = 3;

// Backoff between attempts on the same block. Short, because the whole run is
// waiting on this worker.
const retryDelay = (attempt) => attempt * 1500;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {File|Blob} file         the file to upload
 * @param {function}  uploadChunk  (blob, orderId, totalChunks) => Promise. Must
 *                                 reject on failure, otherwise its worker stalls
 *                                 forever. totalChunks is identical for every
 *                                 block; call sites whose endpoint wants to know
 *                                 up front how many blocks to expect forward it.
 * @param {string}   [chunkType]   when set, each block is wrapped in a Blob of
 *                                 this type (some call sites send typed blocks)
 * @param {function} [onProgress]  (percent, completed, total) => void
 * @param {number}   [attemptsPerBlock] how many times to send a block before
 *                                 giving up on it and failing the run
 * @param {function} [isCancelled] () => boolean. Polled before each block and
 *                                 before each progress report; once it returns
 *                                 true the run stops claiming blocks and goes
 *                                 quiet. Call sites pass one so a closed modal
 *                                 or a superseded run cannot keep driving their
 *                                 progress state.
 * @param {boolean}  [primeFirstBlock] send block 1 alone before parallelising;
 *                                 see the comment at the call below
 * @returns {Promise<{totalBlocksCount: number}>} resolves once every block is up
 */
export const uploadFileInChunks = async ({
  file,
  uploadChunk,
  chunkSize = CHUNK_SIZE,
  concurrency = CHUNK_CONCURRENCY,
  chunkType,
  onProgress,
  isCancelled,
  attemptsPerBlock = CHUNK_ATTEMPTS,
  primeFirstBlock = true,
}) => {
  const size = file?.size || 0;
  const totalBlocksCount = Math.max(1, Math.ceil(size / chunkSize));

  let nextBlockIndex = 0;
  let completed = 0;

  // A failed block rejects Promise.all, but the other workers are still awaiting
  // their own requests - nothing unwinds them. Left alone they keep claiming
  // blocks and keep calling onProgress, so when the user retries after a failure
  // the abandoned run and the new one both drive the same progress state and the
  // bar flips between their two completion counts. This flag retires a run at its
  // first failure: no further blocks are claimed and whatever is already in
  // flight lands silently.
  let abandoned = false;
  const stopped = () => abandoned || (isCancelled ? isCancelled() : false);

  const uploadOne = async (index) => {
    if (stopped()) {
      return;
    }
    const blockStart = index * chunkSize;
    const blockEnd = Math.min(blockStart + chunkSize, size);
    const block = chunkType
      ? new Blob([file.slice(blockStart, blockEnd)], { type: chunkType })
      : file.slice(blockStart, blockEnd);

    // orderId stays 1-based to match what the backend already expects.
    // A blob slice can be read more than once, so the same block is re-sent as
    // is on a retry.
    for (let attempt = 1; ; attempt += 1) {
      try {
        await uploadChunk(block, index + 1, totalBlocksCount);
        break;
      } catch (error) {
        if (stopped()) {
          return;
        }
        if (attempt >= attemptsPerBlock) {
          throw error;
        }
        await wait(retryDelay(attempt));
        if (stopped()) {
          return;
        }
      }
    }

    completed += 1;
    if (onProgress && !stopped()) {
      onProgress(
        Math.round((completed / totalBlocksCount) * 100),
        completed,
        totalBlocksCount
      );
    }
  };

  // UploadChunk opens the S3 multipart upload lazily, on whichever block arrives
  // first, and stores the resulting uploadId against the fileName. Blocks that
  // arrive before that record exists fail with "Cannot read properties of
  // undefined (reading 'uploadId')" - which never happened while uploads were
  // sequential, because block 1 always landed alone. Sending block 1 by itself
  // opens the upload; everything after it can then go out in parallel. Costs one
  // serial round trip out of N, so the speed-up is essentially unaffected.
  if (primeFirstBlock && totalBlocksCount > 1) {
    try {
      await uploadOne(0);
    } catch (error) {
      abandoned = true;
      throw error;
    }
    nextBlockIndex = 1;
  }

  const worker = async () => {
    // Claim-and-increment is atomic here: JS is single-threaded and there is no
    // await between the read and the increment, so no two workers get the same
    // block.
    while (nextBlockIndex < totalBlocksCount && !stopped()) {
      const index = nextBlockIndex;
      nextBlockIndex += 1;
      try {
        await uploadOne(index);
      } catch (error) {
        abandoned = true;
        throw error;
      }
    }
  };

  const remaining = totalBlocksCount - nextBlockIndex;
  const workers = [];
  for (let i = 0; i < Math.min(concurrency, remaining); i += 1) {
    workers.push(worker());
  }

  // Rejects as soon as any block fails, which is what the callers want: the
  // merge must not fire against a partial upload.
  await Promise.all(workers);

  // A cancelled run must not resolve: callers merge on resolve, and merging a
  // half-sent file is worse than reporting the abort.
  if (stopped()) {
    throw new Error("Upload cancelled");
  }

  return { totalBlocksCount };
};
